/**
 * @typedef {import("@store/reducers/notes.reducer").Note} Note
 * @typedef {import("@store/reducers/notes.reducer").NoteAttributeValue} NoteAttributeValue
 */

/**
 * @typedef {object} NoteFilters
 * Every part is optional. An empty or missing part does not restrict.
 * @property {string} [text] Substring of the body, ignoring case.
 * @property {number[]} [playerIds] Keep notes of these players.
 * @property {boolean} [teamNotes] Keep the notes that have no player.
 * @property {string[]} [authorUuids] Keep notes by these authors.
 * @property {string[]} [tags] Keep notes that have ANY of these tags, ignoring case.
 * @property {number[]} [attributeIds] Keep notes that have a value for ANY of these attributes.
 * @property {{ id: number, min?: number | null, max?: number | null } | null} [attribute]
 *   Keep notes with a value for `id`. `min` and `max` are inclusive bounds.
 */

/**
 * @typedef {object} VisualizerColumn
 * @property {string} name
 * @property {string[]} tags
 * @property {number | null} highlightAttributeId
 */

/**
 * The state of the filter controls. `players` holds player ids and, for the "General Notes" choice, `TEAM_NOTES_OPTION`.
 * @typedef {object} NoteFilterState
 * @property {string} text
 * @property {(number | "team")[]} players
 * @property {string[]} tags
 * @property {string[]} authorUuids
 * @property {number[]} attributeIds
 * @property {number | null} attributeId
 * @property {number | null} min
 * @property {number | null} max
 */

/**
 * What a note card shows besides the body.
 * @typedef {object} NoteCardView
 * @property {string} authorLabel The author name, with "(You)" after it for the signed-in user, or "Unknown".
 * @property {string[]} readers The names of the people who can read a targeted note, the author first. Empty for an open note.
 * @property {{ id: number, name: string, value: number }[]} attributes Only the attributes that are in the attribute list.
 */

export const TEAM_NOTES_OPTION = "team";

/** @type {NoteFilterState} */
export const EMPTY_NOTE_FILTERS = { text: "", players: [], tags: [], authorUuids: [], attributeIds: [], attributeId: null, min: null, max: null };

const lowerAll = (list) => (list ?? []).map((s) => s.toLowerCase());

/**
 * True when the note has EVERY tag, ignoring case. An empty list is true.
 * @param {Note} note
 * @param {string[]} tags
 * @returns {boolean}
 */
export const noteHasAllTags = (note, tags) => {
  const have = lowerAll(note.tags);
  return lowerAll(tags).every((tag) => have.includes(tag));
};

const hasAnyTag = (note, lowerTags) => {
  const have = lowerAll(note.tags);
  return lowerTags.some((tag) => have.includes(tag));
};

const matchesAttribute = (note, { id, min, max }) => {
  const entry = note.attributes.find((a) => a.id === id);
  if (!entry) return false;
  return (min == null || entry.value >= min) && (max == null || entry.value <= max);
};

/**
 * Applies the client-side filters. All parts combine with AND.
 * @param {Note[]} notes
 * @param {NoteFilters} [filters]
 * @returns {Note[]}
 */
export const filterNotes = (notes, filters = {}) => {
  const { text, playerIds, teamNotes, authorUuids, tags, attributeIds, attribute } = filters;
  const needle = (text ?? "").toLowerCase();
  const players = playerIds ?? [];
  const authors = authorUuids ?? [];
  const wantedTags = lowerAll(tags);
  const checks = [
    needle && ((n) => n.body.toLowerCase().includes(needle)),
    (players.length > 0 || teamNotes) && ((n) => (n.player ? players.includes(n.player.id) : Boolean(teamNotes))),
    authors.length > 0 && ((n) => authors.includes(n.author?.uuid)),
    wantedTags.length > 0 && ((n) => hasAnyTag(n, wantedTags)),
    attributeIds?.length > 0 && ((n) => n.attributes.some((a) => attributeIds.includes(a.id))),
    attribute && ((n) => matchesAttribute(n, attribute)),
  ].filter(Boolean);

  return notes.filter((note) => checks.every((check) => check(note)));
};

/**
 * Builds one Visualizer row for each player, in the order of `players`.
 * Cell `i` holds the notes of the player that have every tag of `columns[i]`, in the order of `notes`.
 * A player with no notes still gets a row. A team note never shows in a row.
 * @template {{ id: number }} P
 * @param {Note[]} notes
 * @param {P[]} players
 * @param {VisualizerColumn[]} columns
 * @returns {{ player: P, cells: Note[][] }[]}
 */
export const buildVisualizerRows = (notes, players, columns) =>
  players.map((player) => {
    const own = notes.filter((n) => n.player?.id === player.id);
    return { player, cells: columns.map((column) => own.filter((n) => noteHasAllTags(n, column.tags))) };
  });

/**
 * Splits the attribute values of a note into the highlighted value and the rest.
 * With a null `attributeId`, or when the note has no such value, `highlight` is null.
 * An entry keeps all its keys, so a list of named values stays named.
 * @template {NoteAttributeValue} T
 * @param {{ attributes: T[] }} note
 * @param {number | null} attributeId
 * @returns {{ highlight: number | null, others: T[] }}
 */
export const splitHighlight = (note, attributeId) => ({
  highlight: note.attributes.find((a) => a.id === attributeId)?.value ?? null,
  others: note.attributes.filter((a) => a.id !== attributeId),
});

/**
 * Turns the state of the filter controls into the argument of `filterNotes`.
 * On a Player page, `fixedPlayerId` replaces the player choice.
 * @param {NoteFilterState} state
 * @param {number} [fixedPlayerId]
 * @returns {NoteFilters}
 */
export const resolveNoteFilters = (state, fixedPlayerId) => ({
  text: state.text,
  playerIds: fixedPlayerId == null ? state.players.filter((p) => p !== TEAM_NOTES_OPTION) : [fixedPlayerId],
  teamNotes: fixedPlayerId == null && state.players.includes(TEAM_NOTES_OPTION),
  authorUuids: state.authorUuids,
  tags: state.tags,
  attributeIds: state.attributeIds,
  attribute: state.attributeId == null ? null : { id: state.attributeId, min: state.min, max: state.max },
});

/**
 * The options of the author filter: "Me" first, then the other authors of the loaded notes, sorted by name.
 * @param {Note[]} notes
 * @param {string | undefined} myUuid The uuid of the signed-in user. Without it, there is no "Me" option.
 * @returns {{ value: string, label: string }[]}
 */
export const buildAuthorOptions = (notes, myUuid) => {
  const others = new Map();
  for (const { author } of notes) {
    if (author && author.uuid !== myUuid) others.set(author.uuid, author.name);
  }
  const sorted = [...others]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
  return myUuid ? [{ value: myUuid, label: "Me" }, ...sorted] : sorted;
};

/**
 * @param {Note} note
 * @param {{ id: number, name: string }[]} attributes The attribute list of the team.
 * @param {string | undefined} myUuid The uuid of the signed-in user.
 * @returns {NoteCardView}
 */
export const buildNoteCardView = (note, attributes, myUuid) => {
  const nameById = new Map(attributes.map((a) => [a.id, a.name]));
  const labelOf = (user) => (user.uuid === myUuid ? `${user.name} (You)` : user.name);
  const readers = note.visibility === "targeted" ? [note.author, ...note.audience].filter(Boolean).map(labelOf) : [];
  return {
    authorLabel: note.author ? labelOf(note.author) : "Unknown",
    readers,
    attributes: note.attributes.filter((a) => nameById.has(a.id)).map((a) => ({ id: a.id, name: nameById.get(a.id), value: a.value })),
  };
};

/**
 * The players that have a row in the Visualizer: the players of the player filter, in the order of `players`,
 * or every player when the filter names none. On a Player page, `resolveNoteFilters` makes the filter name that player.
 * The "General Notes" choice adds no row, because a general note belongs to no player.
 * @template {{ id: number }} P
 * @param {P[]} players The players of the team, in the team order.
 * @param {{ playerIds?: number[] }} filters The result of `resolveNoteFilters`.
 * @returns {P[]}
 */
export const visualizerPlayers = (players, filters) => {
  const chosen = filters.playerIds ?? [];
  return chosen.length > 0 ? players.filter((p) => chosen.includes(p.id)) : players;
};

/** @type {VisualizerColumn} */
export const NEW_VISUALIZER_COLUMN = { name: "", tags: [], highlightAttributeId: null };

/**
 * The rows that have at least one note in any column.
 * @template R
 * @param {(R & { cells: Note[][] })[]} rows
 */
export const rowsWithNotes = (rows) => rows.filter((row) => row.cells.some((cell) => cell.length > 0));

/** The built-in preset of `PresetSelection`. It loads `DEFAULT_VISUALIZER_COLUMNS`. */
export const DEFAULT_PRESET_NAME = "Default";

/** @type {VisualizerColumn[]} */
export const DEFAULT_VISUALIZER_COLUMNS = [{ ...NEW_VISUALIZER_COLUMN, name: "All notes" }];

/**
 * The display options of the Visualizer. A preset saves them with the columns.
 * @typedef {object} VisualizerOptions
 * @property {boolean} hideEmpty Hide the players with no note in any column.
 * @property {boolean} truncateNotes Cut a long note after 4 lines.
 */

/**
 * The config of a Visualizer preset.
 * @typedef {VisualizerOptions & { columns: VisualizerColumn[] }} VisualizerConfig
 */

/** @type {VisualizerOptions} */
export const DEFAULT_VISUALIZER_OPTIONS = { hideEmpty: false, truncateNotes: true };

/** @type {VisualizerConfig} */
export const DEFAULT_VISUALIZER_CONFIG = { columns: DEFAULT_VISUALIZER_COLUMNS, ...DEFAULT_VISUALIZER_OPTIONS };

/**
 * Reads the config of a saved preset. A config of another shape, such as a bare column list, loads the default.
 * @param {unknown} config
 * @returns {VisualizerConfig}
 */
export const readVisualizerConfig = (config) =>
  Array.isArray(config?.columns) && config.columns.length > 0
    ? { columns: config.columns, hideEmpty: config.hideEmpty === true, truncateNotes: config.truncateNotes !== false }
    : DEFAULT_VISUALIZER_CONFIG;

/**
 * The name of a column, or "Untitled" when the name is blank.
 * @param {VisualizerColumn} column
 * @returns {string}
 */
export const columnLabel = (column) => column.name.trim() || "Untitled";

/**
 * The text of the collapsed column editor, for example "3 columns: Fall Goals, Fall Status, Fall Grades".
 * @param {VisualizerColumn[]} columns
 * @returns {string}
 */
export const summarizeColumns = (columns) =>
  `${columns.length} ${columns.length === 1 ? "column" : "columns"}: ${columns.map(columnLabel).join(", ")}`;

/**
 * The title of the dialog that a "+ Add note" button of a cell opens.
 * @param {string} playerName
 * @param {VisualizerColumn} column
 * @returns {string}
 */
export const cellDialogTitle = (playerName, column) => `New note · ${playerName} · ${columnLabel(column)}`;

/**
 * A note as the Player Notes widget picker reads it. `player_notes_id` is the uuid of the note.
 * @typedef {object} PickerNote
 * @property {string} player_notes_id
 * @property {string} notes
 * @property {string} player_name
 * @property {string | null} author_name
 * @property {string} timestamp
 */

/**
 * The notes that the Player Notes widget picker lists. The widget shows notes of players, so a team note drops out.
 * @param {Note[]} notes
 * @returns {PickerNote[]}
 */
export const toPickerNotes = (notes) =>
  notes
    .filter((n) => n.player !== null)
    .map((n) => ({
      player_notes_id: n.uuid,
      notes: n.body,
      player_name: n.player.name,
      author_name: n.author?.name ?? null,
      timestamp: n.created_at,
    }));

/**
 * How many chips fit on one line, up to `max`. The chips that do not fit go in one "+N" chip.
 * @param {number[]} chipWidths The width of each chip, in order.
 * @param {number} restWidth The width of the "+N" chip.
 * @param {number} gap The space between two chips.
 * @param {number} available The width of the line.
 * @param {number} max The most chips to show.
 * @returns {number}
 */
export const fitChipCount = (chipWidths, restWidth, gap, available, max) => {
  for (let count = Math.min(chipWidths.length, max); count > 0; count--) {
    const chips = chipWidths.slice(0, count).reduce((sum, width) => sum + width, 0) + gap * (count - 1);
    const rest = count < chipWidths.length ? gap + restWidth : 0;
    if (chips + rest <= available) return count;
  }
  return 0;
};
